// ─── 🪪 La tessera associativa ───────────────────────────────────────────────
//
// 16/10/2026, mockup «Tessera associativa» (v9), approvato col «vai».
//
//   · Il modulo è esterno (WindDoc, il link sta nelle Impostazioni): il socio lo
//     apre da «👤 Profilo › 🪪 Tessera associativa» nel bot. Nessun obbligo, e
//     nessun avviso a chi non l'ha fatta.
//   · Compilato il modulo, WindDoc manda alla casella del circolo una mail
//     «Nuova Iscrizione» con nome, cognome ed email. Il gestionale la legge ogni
//     5 minuti e la abbina al socio: da lì la tessera vale **un anno**.
//   · Al socio arriva «✅ Fatto: la tua tessera associativa è registrata…», e
//     **7 giorni prima** della scadenza il promemoria, una volta sola.
//
// 📄 23/10/2026, mockup «Tessera e certificato medico» (*«1A 2sì 3A 4A 5no 6A»*, la 7 B):
//   · la tessera costa **15 € l'anno**: pagata con la carta dal bot (sul gestionale di prova una simulazione) o in
//     Segreteria. Le tessere gratis di prima valgono fino alla loro scadenza (3A);
//   · il **certificato medico** si manda dal bot entro **60 giorni** dal pagamento (per chi aveva già la tessera, dal
//     giorno in cui la regola si accende: 4A). Caricato, sblocca subito (1A); scaduto, ferma come quello mancante
//     (2sì). La data di rilascio la scrive il socio (7B), la scadenza è un anno dopo;
//   · con «Serve la tessera per prenotare» acceso, dal bot prenota (campo, lezione, fisso: 5no) solo chi ha la
//     tessera pagata e non è in pausa. 🪪 01/11/2026: la lezione no, si prenota anche senza; la partita (campo e fisso) sì.
import { z } from 'zod';
import { piuGiorni } from './calendario.js';
import { dataConAnno } from './organizzatore.js';
/** Il modulo di partenza, dato dal committente il 15/10/2026. */
export const MODULO_DI_PARTENZA = 'https://www.sportstudiolamanna.it/padel-village.html';
/** Un anno dalla compilazione del modulo. */
export const MESI_DELLA_TESSERA = 12;
/** Il promemoria, una volta sola. */
export const GIORNI_DEL_PROMEMORIA = 7;
/** Chi manda la mail, e il suo oggetto: le altre mail della casella non si leggono. */
export const MITTENTE_WINDDOC = 'noreply@winddoc.com';
export const OGGETTO_WINDDOC = 'Nuova Iscrizione';
/** Un tocco su «📝 Compila il modulo» vale per abbinare la mail se è di questi ultimi giorni. */
export const GIORNI_DEL_TOCCO = 14;
/** 📄 Il prezzo di partenza: 15 € l'anno. */
export const PREZZO_TESSERA_DI_PARTENZA = 1500;
/** 📄 Il certificato si manda entro 60 giorni dal pagamento della tessera (01/11/2026: 🗣️ «Mettine 60, non 30»). */
export const GIORNI_PER_IL_CERTIFICATO = 60;
/** 📄 Il promemoria del certificato da mandare: 7 giorni prima del trentesimo. */
export const GIORNI_PROMEMORIA_CERTIFICATO = 7;
/** 📄 I promemoria del certificato che scade: 30 e 7 giorni prima. */
export const GIORNI_PROMEMORIA_SCADENZA_CERTIFICATO = [30, 7];
/** 📄 Il file del certificato: una foto o un PDF, fino a 8 MB. */
export const MASSIMO_CERTIFICATO = 8 * 1024 * 1024;
export const TIPI_DEL_CERTIFICATO = ['image/jpeg', 'image/png', 'application/pdf'];
const giornoSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const impostazioniTesseraSchema = z.object({
    /** «Mostra la tessera nel bot»: spenta, la voce in Profilo non c'è. */
    attiva: z.boolean(),
    /** Il link del modulo, solo `https://`. */
    linkModulo: z
        .string()
        .trim()
        .url()
        .refine((u) => u.startsWith('https://'), 'Il link del modulo deve cominciare con https://'),
    // 📄 23/10/2026. I `default` lasciano valide le impostazioni scritte prima.
    /** Il prezzo all'anno, in centesimi. */
    prezzoCents: z.number().int().min(0).max(100_000).default(PREZZO_TESSERA_DI_PARTENZA),
    /** «Serve la tessera per prenotare dal bot»: spento, tutto come prima. */
    servePerPrenotare: z.boolean().default(false),
    /** Il giorno in cui la regola si è accesa la prima volta: per chi aveva già la tessera i 60 giorni partono da qui (4A). Lo scrive il gestionale. */
    accesaIl: giornoSchema.nullable().default(null),
    /** 🧪 «Pagamento con la carta: simulazione», solo per il gestionale di prova: nessuna carta, un incasso segnato «simulato». */
    pagamentoSimulato: z.boolean().default(false),
});
export const IMPOSTAZIONI_TESSERA_DI_PARTENZA = {
    attiva: true,
    linkModulo: MODULO_DI_PARTENZA,
    prezzoCents: PREZZO_TESSERA_DI_PARTENZA,
    servePerPrenotare: false,
    accesaIl: null,
    pagamentoSimulato: false,
};
/** Come la vede la Segreteria, con lo stato della casella delle mail. */
export const paginaTesseraSchema = impostazioniTesseraSchema.extend({
    /** La casella si legge solo se il team di deploy ha messo le credenziali. */
    casellaCollegata: z.boolean(),
    /** L'ultima lettura riuscita della casella, se c'è stata. */
    ultimaLettura: z.string().nullable(),
    daAbbinare: z.number().int(),
    /** 📄 I certificati arrivati e non ancora guardati, e i soci in pausa oggi. */
    certificatiDaControllare: z.array(z.object({
        memberId: z.uuid(),
        nome: z.string(),
        arrivato: z.string(),
        rilascio: z.string(),
        validoFino: z.string(),
    })),
    inPausa: z.array(z.object({ memberId: z.uuid(), nome: z.string(), pagataIl: z.string().nullable(), perche: z.string() })),
});
// ── La tessera di un socio ─────────────────────────────────────────────────
export const STATI_TESSERA = ['da_fare', 'attiva', 'scaduta'];
/** 📄 Il certificato com'è oggi. `da_controllare` e `controllato` valgono (1A); gli altri no. */
export const STATI_CERTIFICATO = ['manca', 'da_controllare', 'controllato', 'altra_foto', 'scaduto'];
export const certificatoDelSocioSchema = z.object({
    stato: z.enum(STATI_CERTIFICATO),
    arrivato: z.string().nullable(),
    fonte: z.enum(['bot', 'segreteria']).nullable(),
    rilascio: z.string().nullable(),
    validoFino: z.string().nullable(),
    tipo: z.string().nullable(),
    /** Il motivo dell'«altra foto». */
    nota: z.string().nullable(),
});
export const tesseraDelSocioSchema = z.object({
    stato: z.enum(STATI_TESSERA),
    /** `YYYY-MM-DD`: l'ultimo giorno in cui vale. */
    scade: z.string().nullable(),
    /** Quando è stato compilato il modulo (o quando la Segreteria l'ha segnata). */
    dal: z.string().nullable(),
    /** `mail` (WindDoc) o `segreteria` (corretta a mano). */
    fonte: z.string().nullable(),
    // 📄 23/10/2026
    /** La tessera di questa scadenza è pagata (o era gratis, prima dei 15 €). */
    pagata: z.boolean(),
    pagataIl: z.string().nullable(),
    /** Com'è stata pagata: la riga di cassa (`null` per le tessere gratis di prima). */
    pagamento: z
        .object({ metodo: z.string(), importoCents: z.number().int(), note: z.string().nullable() })
        .nullable(),
    prezzoCents: z.number().int(),
    certificato: certificatoDelSocioSchema,
    /** Il certificato serve entro questo giorno (`null` se la tessera non è pagata). */
    certificatoEntroIl: z.string().nullable(),
    /** Le prenotazioni dal bot sono in pausa (regola accesa, tessera pagata, certificato mancante dopo il trentesimo). */
    inPausa: z.boolean(),
    servePerPrenotare: z.boolean(),
});
/** La Segreteria corregge la data, o toglie la tessera (`scade: null`). */
export const correggiTesseraSchema = z.object({
    scade: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
});
/** 💶 «Incassa 15 €» dalla scheda: contanti o carta (01/11/2026: 🗣️ «La quota non si paga con borsellino»). */
export const incassaTesseraSchema = z.object({
    method: z.enum(['cash', 'card']),
    idempotencyKey: z.string().min(8).max(200),
});
/** 📄 Il certificato caricato: dal bot o dalla Segreteria. Il file in base64. */
export const caricaCertificatoSchema = z.object({
    fileBase64: z
        .string()
        .min(1)
        .max(Math.ceil((MASSIMO_CERTIFICATO * 4) / 3) + 8),
    mime: z.enum(TIPI_DEL_CERTIFICATO),
    fileName: z.string().trim().min(1).max(200),
    /** La data di rilascio, scritta dal socio (7B). */
    issuedOn: giornoSchema,
});
/** 📄 La Segreteria guarda il certificato: va bene, la data è da correggere, o serve un'altra foto. */
export const controllaCertificatoSchema = z.discriminatedUnion('esito', [
    z.object({ esito: z.literal('va_bene') }),
    z.object({ esito: z.literal('data'), rilascio: giornoSchema }),
    z.object({ esito: z.literal('altra_foto'), motivo: z.string().trim().min(3).max(200) }),
]);
export function statoDellaTessera(scade, oggi) {
    if (!scade)
        return 'da_fare';
    return scade >= oggi ? 'attiva' : 'scaduta';
}
/** L'ultimo giorno della tessera: lo stesso giorno, un anno dopo (modulo il 15/10/2026 ⇒ fino al 15/10/2027, come nel mockup). */
export function scadenzaDa(giorno) {
    const [a, m, g] = giorno.split('-').map(Number);
    const anno = a + MESI_DELLA_TESSERA / 12;
    // Il 29 febbraio diventa il 28: un anno dopo, lo stesso giorno o l'ultimo del mese.
    const ultimo = new Date(Date.UTC(anno, m, 0)).getUTCDate();
    return `${anno}-${String(m).padStart(2, '0')}-${String(Math.min(g, ultimo)).padStart(2, '0')}`;
}
/** Il giorno in cui parte il promemoria: 7 giorni prima della scadenza. */
export function giornoDelPromemoria(scade) {
    const [a, m, g] = scade.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, g - GIORNI_DEL_PROMEMORIA)).toISOString().slice(0, 10);
}
/** Il giorno di Roma di un istante. */
export function giornoARoma(istante) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(istante);
}
/** «15 €», «12,50 €». */
export function euroDellaTessera(cents) {
    return cents % 100 === 0 ? `${cents / 100} €` : `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}
const DOVE_SI_FA = '«👤 Profilo › 🪪 Tessera associativa»';
/**
 * ⭐ Il sì/no della tessera e del certificato si calcola **qui**: lo usano il ponte (prenotare, la sezione del bot),
 * la scheda e il giro dei promemoria, e una regola scritta due volte diverge.
 */
export function verdettoTessera(s, imp, oggi) {
    const base = statoDellaTessera(s.scade, oggi);
    const pagata = base === 'attiva' && s.pagataFino !== null && s.pagataFino === s.scade;
    const tessera = base === 'attiva' && !pagata ? 'da_pagare' : base;
    const c = s.certificato;
    const certificato = !c
        ? 'manca'
        : c.stato === 'altra_foto'
            ? 'altra_foto'
            : c.validoFino < oggi
                ? 'scaduto'
                : c.stato;
    const certificatoValido = certificato === 'da_controllare' || certificato === 'controllato';
    let entroIl = null;
    if (tessera === 'attiva') {
        const dal = s.pagataIl ?? oggi;
        // 4A: per chi aveva già la tessera, i 60 giorni partono da quando la regola si accende.
        const partenza = imp.accesaIl !== null && imp.accesaIl > dal ? imp.accesaIl : dal;
        entroIl = piuGiorni(partenza, GIORNI_PER_IL_CERTIFICATO);
    }
    const inPausa = imp.servePerPrenotare && tessera === 'attiva' && !certificatoValido && oggi > entroIl;
    let motivo = null;
    let primaSenzaTessera = false;
    if (imp.servePerPrenotare) {
        const prezzo = euroDellaTessera(imp.prezzoCents);
        // 🪪 01/11/2026 (mockup «La prima partita senza tessera», A: 🗣️ *«Ti fa prenotare la prima partita da non tesserato
        // … anche se ti è scaduto … dalla seconda partita non puoi più prenotare. Né una lezione, né una partita»*): una
        // prenotazione sola senza tessera, contata dall'ultima tessera valida. Prima la quota, poi il modulo.
        const senza = tessera === 'da_fare' || tessera === 'scaduta' || tessera === 'da_pagare';
        if (senza) {
            const finoA = tessera === 'scaduta' ? s.scade : tessera === 'da_pagare' ? s.pagataFino : null;
            const usata = !!s.senzaTesseraIl && (finoA === null || s.senzaTesseraIl > finoA);
            if (!usata)
                primaSenzaTessera = true;
            else if (tessera === 'da_fare')
                motivo = `Per prenotare serve la tessera associativa: ${prezzo} per un anno. La prenotazione senza tessera l’hai già fatta. Il primo passo è la quota: con la carta da ${DOVE_SI_FA}, oppure in Segreteria.`;
            else if (tessera === 'scaduta')
                motivo = `Per prenotare la tessera associativa è da rinnovare: ${prezzo} per un anno. La prenotazione senza tessera l’hai già fatta. Il primo passo è la quota: con la carta da ${DOVE_SI_FA}, oppure in Segreteria.`;
            else
                motivo = `Per prenotare manca solo il pagamento della tessera, ${prezzo}: da ${DOVE_SI_FA}, oppure in Segreteria.`;
        }
        else if (inPausa)
            motivo =
                certificato === 'scaduto'
                    ? `Le prenotazioni ripartono appena arriva il certificato medico nuovo: basta una foto da ${DOVE_SI_FA}.`
                    : `Le prenotazioni ripartono appena arriva il certificato medico: basta una foto da ${DOVE_SI_FA}.`;
    }
    return { tessera, certificato, entroIl, inPausa, puoPrenotare: motivo === null, motivo, primaSenzaTessera };
}
/** 📄 La data di rilascio scritta dal socio: già passata, e il certificato ancora valido. `null` ⇒ va bene. */
export function rilascioDaRifiutare(rilascio, oggi) {
    if (rilascio > oggi)
        return 'La data di rilascio è quella già passata, scritta sul certificato vicino al timbro del medico: riscrivila così, 20/10/2026.';
    if (scadenzaDa(rilascio) < oggi)
        return 'Con questa data il certificato è già scaduto: serve quello nuovo, rilasciato nell’ultimo anno. Quando ce l’hai, mandami la foto.';
    return null;
}
// ── Le frasi per il socio (le manda il bot com'è) ───────────────────────────
/** 🪪 Appena la mail di WindDoc è abbinata. */
export function fraseTesseraRegistrata(scade) {
    return `✅ Fatto: la tua tessera associativa è registrata, valida fino al ${dataConAnno(scade)}.`;
}
/** ⏰ 7 giorni prima, una volta sola. Il bottone «📝 Compila il modulo» lo mette il bot. */
export function fraseTesseraScade(scade) {
    const giorno = dataConAnno(scade).replace(/ \d{4}$/, '');
    return `⏰ La tua tessera associativa scade il ${giorno}.\nPer rinnovarla, compila il modulo con il link qui sotto.`;
}
/** 💶 La tessera pagata. Se il certificato manca, l'ultimo passo. */
export function fraseTesseraPagata(scade, certificatoEntroIl) {
    const prima = `✅ Fatto: la tua tessera associativa è valida fino al ${dataConAnno(scade)}.`;
    if (!certificatoEntroIl)
        return prima;
    const giorno = dataConAnno(certificatoEntroIl).replace(/ \d{4}$/, '');
    return `${prima}\nUltimo passo: manda il certificato medico entro il ${giorno}, con «📄 Carica il certificato medico».`;
}
/** 📄 Il certificato è arrivato. */
export function fraseCertificatoRicevuto(validoFino) {
    return `✅ Fatto: certificato medico ricevuto, valido fino al ${dataConAnno(validoFino)}. Grazie!`;
}
/** ⏰ 7 giorni prima del trentesimo, una volta sola. Il bottone lo mette il bot. */
export function fraseCertificatoManca(entroIl) {
    const giorno = dataConAnno(entroIl).replace(/ \d{4}$/, '');
    return `⏰ Ricordati del certificato medico: va mandato entro il ${giorno}.\nBasta una foto, dal bottone qui sotto: così continui a prenotare senza pensieri.`;
}
/** ⏸️ Il giorno dopo il trentesimo, una volta sola. */
export function fraseInPausa() {
    return '⏸️ Le prenotazioni dal bot sono in pausa finché arriva il certificato medico.\nAppena lo carichi, ripartono subito.';
}
/** 📄 La Segreteria chiede un'altra foto. */
export function fraseAltraFoto(motivo) {
    return `📄 Serve un’altra foto del certificato medico: «${motivo}».\nPuoi rimandarla dal bottone qui sotto.`;
}
/** ⏰ 30 e 7 giorni prima della scadenza del certificato, una volta sola ciascuno. */
export function fraseCertificatoScade(validoFino) {
    const giorno = dataConAnno(validoFino).replace(/ \d{4}$/, '');
    return `⏰ Il tuo certificato medico scade il ${giorno}.\nPer continuare a prenotare, manda quello nuovo dal bottone qui sotto.`;
}
/** ⏸️ Il certificato scaduto (2sì), una volta sola. */
export function fraseCertificatoScaduto(validoFino) {
    const giorno = dataConAnno(validoFino).replace(/ \d{4}$/, '');
    return `⏸️ Il certificato medico è scaduto il ${giorno}: le prenotazioni dal bot sono in pausa finché arriva quello nuovo.\nBasta una foto, dal bottone qui sotto.`;
}
/** Il corpo della mail in righe di testo, anche se arriva in HTML. */
export function testoDellaMail(corpo) {
    const testo = corpo
        .replace(/<(br|\/p|\/div|\/tr|\/td|\/th|\/li|\/h\d)\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&#39;|&apos;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
    return testo
        .split(/\r?\n/)
        .map((r) => r.replace(/\s+/g, ' ').trim())
        .filter((r) => r !== '');
}
/**
 * Nome, cognome ed email dalla mail «Nuova Iscrizione». Le etichette possono
 * stare sulla stessa riga («Nome: Luca») o sopra il valore («Nome» e sotto
 * «Luca»). Senza nome e cognome ⇒ `null`: la mail non è un'iscrizione.
 */
export function leggiIscrizione(corpo) {
    const righe = testoDellaMail(corpo);
    const valore = (etichetta) => {
        const re = new RegExp(`^${etichetta}\\s*[:\\-–]?\\s*(.*)$`, 'i');
        for (let i = 0; i < righe.length; i++) {
            const r = righe[i].match(re);
            if (!r)
                continue;
            const stessaRiga = r[1].trim();
            if (stessaRiga !== '')
                return stessaRiga;
            const sotto = righe[i + 1];
            if (sotto && !/^(nome|cognome|e-?mail)\b/i.test(sotto))
                return sotto.trim();
        }
        return '';
    };
    const nome = valore('nome');
    const cognome = valore('cognome');
    const email = valore('e-?mail').toLowerCase();
    if (nome === '' || cognome === '')
        return null;
    return { nome, cognome, email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '' };
}
/** Nome e cognome confrontabili: minuscole, senza accenti, apostrofi e spazi doppi. */
export function nomeConfrontabile(s) {
    return s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[’'`]/g, '')
        .replace(/[^a-z]+/g, ' ')
        .trim();
}
// ── Le mail da abbinare (Telegram › 🪪 Tessere da abbinare) ─────────────────
export const mailTesseraSchema = z.object({
    id: z.uuid(),
    ricevuta: z.string(),
    nome: z.string(),
    cognome: z.string(),
    email: z.string(),
    stato: z.enum(['abbinata', 'da_abbinare', 'ignorata', 'doppia']),
    socio: z.object({ id: z.uuid(), nome: z.string() }).nullable(),
});
export const elencoMailTesseraSchema = z.object({ items: z.array(mailTesseraSchema) });
export const abbinaMailSchema = z.object({ memberId: z.uuid() });
// ── Il ponte del bot ─────────────────────────────────────────────────────────
/** `GET /players/:memberId/membership-card`. */
export const tesseraPerIlBotSchema = z.object({
    /** «Mostra la tessera nel bot»: falso ⇒ la voce in Profilo non c'è. */
    visible: z.boolean(),
    status: z.enum(['none', 'active', 'expired']),
    /** `YYYY-MM-DD`, l'ultimo giorno in cui vale. */
    until: z.string().nullable(),
    /** Il link che passa dal gestionale (segna il tocco) e apre il modulo: solo a quota pagata (01/11/2026). */
    formUrl: z.string().nullable(),
    // 📄 23/10/2026: campi in più, i vecchi restano com'erano.
    /** Il prezzo all'anno, in centesimi. */
    price: z.number().int(),
    /** La tessera di questa scadenza è pagata. */
    paid: z.boolean(),
    /** «💳 Paga»: la pagina del pagamento con la carta. `null` ⇒ si paga in Segreteria. */
    payUrl: z.string().nullable(),
    /** Sul gestionale di prova il pagamento è una simulazione. */
    paySimulated: z.boolean(),
    /** 🪪 01/11/2026: falso ⇒ la quota è pagata e il modulo è ancora da compilare (c'è `formUrl`). */
    formReceived: z.boolean(),
    certificate: z.object({
        status: z.enum(['none', 'pending', 'ok', 'retake', 'expired']),
        /** `YYYY-MM-DD`: il certificato vale fino a qui. */
        until: z.string().nullable(),
        /** `YYYY-MM-DD`: il certificato serve entro qui. */
        deadline: z.string().nullable(),
        /** Il motivo per cui serve un'altra foto. */
        retakeReason: z.string().nullable(),
    }),
    /** «Serve la tessera per prenotare» è acceso. */
    required: z.boolean(),
    /** Le prenotazioni dal bot sono in pausa (certificato da mandare). */
    paused: z.boolean(),
    canBook: z.boolean(),
    /** Perché non può prenotare, già scritto per il socio. */
    reason: z.string().nullable(),
    /** 🪪 01/11/2026: la prossima prenotazione (partita o lezione) è quella senza tessera; dopo serve la tessera. */
    firstWithoutCard: z.boolean(),
});
/** 📄 `POST /players/:memberId/medical-certificate`: il verdetto, e la tessera com'è dopo. */
export const certificatoDalPonteSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato', 'non_lo_so']),
    reason: z.string().nullable(),
    /** La frase per il socio quando è fatto. */
    text: z.string().nullable(),
    validUntil: z.string().nullable(),
});
//# sourceMappingURL=tessera.js.map